import axios from 'axios';

const FDA_BASE = 'https://api.fda.gov';
const TRIALS_BASE = 'https://clinicaltrials.gov/api/v2';

// Optional openFDA API key — raises the rate limit from 240 req/min (shared
// per IP) to 240 req/min per key with a much higher daily cap. Set
// REACT_APP_OPENFDA_KEY in .env (local) or the Render environment (deploy).
const FDA_KEY = process.env.REACT_APP_OPENFDA_KEY;

function withKey(url) {
  if (!FDA_KEY) return url;
  return `${url}${url.includes('?') ? '&' : '?'}api_key=${FDA_KEY}`;
}

const SPELL_CORRECTIONS = {
  'semaglitide': 'semaglutide',
  'semagludite': 'semaglutide',
  'accutane': 'isotretinoin',
  'rogaine': 'minoxidil',
  'propecia': 'finasteride',
  'glucophage': 'metformin',
  'advil': 'ibuprofen',
  'tylenol': 'acetaminophen',
  'zoloft': 'sertraline',
  'prozac': 'fluoxetine',
  'adderall': 'amphetamine',
  'ritalin': 'methylphenidate',
};

export function getSuggestion(query) {
  const lower = query.toLowerCase().trim();
  if (SPELL_CORRECTIONS[lower]) return SPELL_CORRECTIONS[lower];

  const allKnownTerms = [
    'semaglutide', 'isotretinoin', 'minoxidil', 'finasteride',
    'metformin', 'ibuprofen', 'acetaminophen', 'sertraline',
    'fluoxetine', 'amphetamine', 'methylphenidate',
    ...Object.keys(SPELL_CORRECTIONS)
  ];

  const match = allKnownTerms.find(term => {
    if (Math.abs(term.length - lower.length) > 4) return false;
    return levenshtein(lower, term) <= 3;
  });

  return match ? SPELL_CORRECTIONS[match] || match : null;
}

function levenshtein(a, b) {
  const dp = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[a.length][b.length];
}


export async function searchDrug(drugName) {
  // Step 1: resolve identity — get FDA label to find generic name
  const drugInfo = await getDrugInfo(drugName);

  // Step 2: fetch data using exactly what was searched — brand and generic
  // intentionally return different results (ozempic ≠ semaglutide)
  const [adverseEvents, recalls, trials, brandNames] = await Promise.all([
    getAdverseEvents(drugName),
    getRecalls(drugName),
    getClinicalTrials(drugName),
    drugInfo?.genericName && drugInfo.genericName !== 'Unknown'
      ? getBrandNames(drugInfo.genericName)
      : Promise.resolve([]),
  ]);

  const hasData = drugInfo || adverseEvents.length > 0 || trials.length > 0;

  let didYouMean = null;
  if (!drugInfo && trials.length === 0) {
    const hardcoded = getSuggestion(drugName);
    if (hardcoded && hardcoded.toLowerCase() !== drugName.toLowerCase()) {
      didYouMean = hardcoded;
    } else {
      didYouMean = await getFDASuggestion(drugName);
    }
  }

  return {
    name: drugName,
    drugInfo,
    brandNames,                           // all known brand names for this molecule
    adverseEvents,
    recalls,
    trials,
    conditionMap: buildConditionMap(trials, drugName),
    evidenceTier: calculateEvidenceTier(trials, drugInfo),
    didYouMean,
    hasData,
  };
}

async function getFDASuggestion(drugName) {
  const words = drugName.trim().split(/\s+/).filter(w => w.length >= 4);
  const candidates = [drugName, ...words];

  for (const term of candidates) {
    try {
      const res = await axios.get(withKey(
        `${FDA_BASE}/drug/label.json?search=openfda.brand_name:${encodeURIComponent(term)}*&limit=1`
      ));
      const result = res.data.results?.[0];
      const suggestion = result?.openfda?.brand_name?.[0];
      if (suggestion && suggestion.toLowerCase() !== drugName.toLowerCase()) {
        return suggestion;
      }
    } catch {
      // try next candidate
    }
  }
  return null;
}

async function getDrugInfo(drugName) {
  try {
    const encoded = encodeURIComponent(drugName);
    // Try brand name first, fall back to generic name
    let result = null;
    for (const field of ['openfda.brand_name', 'openfda.generic_name']) {
      try {
        const res = await axios.get(withKey(
          `${FDA_BASE}/drug/label.json?search=${field}:"${encoded}"&limit=1`
        ));
        if (res.data.results?.[0]) { result = res.data.results[0]; break; }
      } catch { /* try next */ }
    }
    if (!result) return null;

    let purpose = result.purpose?.[0] || result.indications_and_usage?.[0] || 'See FDA label';
    purpose = purpose
      .replace(/^purpose\s*/i, '')
      .replace(/^\d+\s+indications and usage\s*/i, '')
      .trim();

    return {
      brandName: result.openfda?.brand_name?.[0] || drugName,
      genericName: result.openfda?.generic_name?.[0] || 'Unknown',
      manufacturer: result.openfda?.manufacturer_name?.[0] || 'Unknown',
      purpose,
      warnings: result.warnings?.[0] || null,
      boxedWarning: result.boxed_warning?.[0] || null,
      contraindications: result.contraindications?.[0] || null,
      drugInteractions: result.drug_interactions?.[0] || null,
      approvalDate: result.openfda?.application_number?.[0] || 'Unknown',
    };
  } catch {
    return null;
  }
}

async function getBrandNames(genericName) {
  try {
    const res = await axios.get(withKey(
      `${FDA_BASE}/drug/label.json?search=openfda.generic_name:"${genericName}"&limit=10`
    ));
    const brands = new Set();
    res.data.results.forEach(r => {
      (r.openfda?.brand_name || []).forEach(b => brands.add(b));
    });
    return Array.from(brands).slice(0, 8);
  } catch {
    return [];
  }
}

async function getAdverseEvents(drugName) {
  try {
    const res = await axios.get(withKey(
      `${FDA_BASE}/drug/event.json?search=patient.drug.medicinalproduct:"${drugName}"&count=patient.reaction.reactionmeddrapt.exact&limit=5`
    ));
    return res.data.results.map(r => ({ reaction: r.term, count: r.count }));
  } catch {
    return [];
  }
}

export async function getRecalls(drugName) {
  try {
    const res = await axios.get(withKey(
      `${FDA_BASE}/drug/enforcement.json?search=product_description:"${drugName}"&limit=5`
    ));
    const seen = new Set();
    return res.data.results
      .map(r => ({
        reason: r.reason_for_recall,
        date: r.recall_initiation_date,
        status: r.status,
      }))
      .filter(r => {
        const key = `${r.reason}|${r.date}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 3);
  } catch {
    return [];
  }
}

async function getClinicalTrials(drugName) {
  try {
    const res = await axios.get(
      `${TRIALS_BASE}/studies?query.term=${encodeURIComponent(drugName)}&pageSize=50&fields=protocolSection`
    );
    const studies = res.data.studies || [];
    return studies.map(s => {
      const proto = s.protocolSection;
      return {
        title: proto?.identificationModule?.briefTitle || 'Untitled',
        phase: proto?.designModule?.phases?.[0] || 'Unknown',
        enrollment: proto?.designModule?.enrollmentInfo?.count || 0,
        status: proto?.statusModule?.overallStatus || 'Unknown',
        startDate: proto?.statusModule?.startDateStruct?.date || 'Unknown',
        completionDate: proto?.statusModule?.completionDateStruct?.date || 'Unknown',
        conditions: proto?.conditionsModule?.conditions || [],
      };
    });
  } catch {
    return [];
  }
}

// Trial "conditions" that aren't real conditions: healthy-volunteer studies
// and entries that just restate the drug being tested
function isNoiseCondition(condition, drugName) {
  const c = condition.toLowerCase();
  if (c === 'healthy' || c.includes('healthy volunteer') || c.includes('healthy subject') || c.includes('healthy participant')) return true;
  if (drugName && c.includes(drugName.toLowerCase())) return true;
  return false;
}

function buildConditionMap(trials, drugName) {
  const map = {};
  trials.forEach(trial => {
    const phase = trial.phase;
    trial.conditions.forEach(condition => {
      if (isNoiseCondition(condition, drugName)) return;
      const normalized = normalizeCondition(condition);
      const key = normalized.toLowerCase();
      const existingKey = Object.keys(map).find(k => levenshtein(k, key) <= 3);
      const target = existingKey || key;
      if (!map[target]) map[target] = { label: normalized, count: 0, phases: new Set() };
      map[target].count += 1;
      if (phase && phase !== 'Unknown') map[target].phases.add(phase);
    });
  });

  return Object.values(map)
    .map(({ label, count, phases }) => ({ condition: label, count, phases: Array.from(phases) }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
}

function normalizeCondition(condition) {
  return condition.toLowerCase().replace(/\s+/g, ' ').trim().replace(/\b\w/g, c => c.toUpperCase());
}

function calculateEvidenceTier(trials, drugInfo) {
  if (!drugInfo && trials.length === 0) {
    return { label: 'No FDA Data', color: '#999', description: 'No FDA approval or clinical trial data found for this product.' };
  }

  const phase3or4 = trials.filter(t => t.phase === 'PHASE3' || t.phase === 'PHASE4').length;
  const largeTrials = trials.filter(t => t.enrollment > 500).length;
  const totalEnrollment = trials.reduce((sum, t) => sum + t.enrollment, 0);

  // Well-Studied requires real scale: multiple late-phase trials AND 2,000+
  // total participants. A single 500-person trial is not enough.
  if (phase3or4 >= 3 && totalEnrollment >= 2000) {
    return { label: 'Well-Studied', color: '#2e7d32', description: 'Multiple large Phase 3 or 4 trials exist. Long-term evidence is available.' };
  } else if (phase3or4 >= 1 && largeTrials >= 1) {
    return { label: 'Moderate Evidence', color: '#f57c00', description: 'Some larger trials exist but long-term or large-scale data may be limited.' };
  } else if (trials.length >= 3 || phase3or4 >= 1) {
    return { label: 'Moderate Evidence', color: '#f57c00', description: 'Some larger trials exist but long-term or large-scale data may be limited.' };
  } else if (trials.length > 0) {
    return { label: 'Thin Evidence', color: '#c62828', description: 'Only early-phase or small trials found. Long-term safety is not well established.' };
  } else if (drugInfo) {
    return { label: 'Thin Evidence', color: '#c62828', description: 'FDA-approved drug, but no clinical trial data was found in ClinicalTrials.gov. Long-term evidence may be limited.' };
  } else {
    return { label: 'No FDA Data', color: '#999', description: 'No FDA approval or clinical trial data found. This may be a supplement or unapproved product.' };
  }
}
